import { alpha, createTheme, darken } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import { darkBlueColors, darkMainColors, lightWarmColors, randolBrandedColors, randolWarmColors, whiteBlueColors } from './colors';
import { appShape } from './shapeTokens';
import { portalScrollbarStyles } from './scrollbars';

declare module '@mui/material/styles' {
	interface TypeBackground {
		sidebar?: string;
		canvas?: string;
		gutter?: string;
		gutterGradientTo?: string;
		header?: string;
	}
	interface Theme {
		layout: {
			edgeGap: number;
			panelGap: number;
		};
	}
	interface ThemeOptions {
		layout?: {
			edgeGap?: number;
			panelGap?: number;
		};
	}

	interface Palette {
		navigation: {
			background: string;
			item: string;
			itemHover: string;
			selectedBackground: string;
			selectedText: string;
			selectedIcon: string;
			sectionLabel: string;
		};
	}

	interface PaletteOptions {
		navigation?: {
			background?: string;
			item?: string;
			itemHover?: string;
			selectedBackground?: string;
			selectedText?: string;
			selectedIcon?: string;
			sectionLabel?: string;
		};
	}
}

export type PortalThemeName = 'dark-main' | 'dark-blue' | 'white-blue' | 'light-warm' | 'randol-warm' | 'randol-branded';

export const portalThemeOptions: { value: PortalThemeName; label: string }[] = [
	{ value: 'dark-main', label: 'Dark Main' },
	{ value: 'dark-blue', label: 'Dark Blue' },
	{ value: 'white-blue', label: 'White Blue' },
	{ value: 'light-warm', label: 'White Warm' },
	{ value: 'randol-warm', label: 'Randol Warm' },
	{ value: 'randol-branded', label: 'Randol Branded' },
];

type PortalColors = {
	mode: 'dark' | 'light';
	primary: { main: string; light?: string };
	secondary: { main: string; dark: string };
	background: { default: string; paper: string; header?: string; sidebar?: string; canvas?: string; gutter?: string; gutterGradientTo?: string; texture?: string };
	text: { primary: string; secondary: string };
	button?: {
		primary?: {
			background?: string;
			text?: string;
			hoverBackground?: string;
		};
	};
	divider?: { default: string };
	navigation?: {
		background?: string;
		item?: string;
		itemHover?: string;
		selectedBackground?: string;
		selectedText?: string;
		selectedIcon?: string;
		sectionLabel?: string;
	};
};

const appFontFamily = '"Poppins", sans-serif';

const createPortalTheme = (colors: PortalColors): Theme => {
	const primaryButtonBackground = colors.button?.primary?.background ?? colors.primary.main;
	const primaryButtonText = colors.button?.primary?.text;
	const primaryButtonHoverBackground =
		colors.button?.primary?.hoverBackground ?? darken(primaryButtonBackground, colors.mode === 'dark' ? 0.15 : 0.12);

	const colorScale = (main: string, light?: string) => ({
		main,
		...(light ? { light } : {}),
		50: alpha(main, 0.1),
		200: alpha(light ?? main, 0.35),
	});
	const theme = createTheme({
		typography: {
			fontFamily: appFontFamily,
		},
		layout: {
			edgeGap: 8,
			panelGap: 8,
		},
		palette: {
			mode: colors.mode,
			primary: { ...colorScale(colors.primary.main, colors.primary.light), ...(primaryButtonText ? { contrastText: primaryButtonText } : {}) } as any,
			secondary: { ...colorScale(colors.secondary.main), dark: colors.secondary.dark } as any,
			info: colorScale(colors.primary.main, colors.primary.light) as any,
			success: colorScale('#43D17D') as any,
			background: {
				default: colors.background.default,
				canvas: colors.background.canvas ?? colors.background.default,
				paper: colors.background.paper,
				header: colors.background.header ?? colors.background.paper,
				sidebar: colors.background.sidebar ?? colors.background.paper,
				gutter: colors.background.gutter ?? colors.background.canvas ?? colors.background.default,
				gutterGradientTo: colors.background.gutterGradientTo ?? colors.background.gutter ?? colors.background.canvas ?? colors.background.default,
			},
			navigation: {
				background: colors.navigation?.background ?? colors.background.sidebar ?? colors.background.default,
				item: colors.navigation?.item ?? colors.text.primary,
				itemHover: colors.navigation?.itemHover ?? alpha(colors.primary.main, colors.mode === 'dark' ? 0.12 : 0.08),
				selectedBackground: colors.navigation?.selectedBackground ?? alpha(colors.primary.main, colors.mode === 'dark' ? 0.24 : 0.14),
				selectedText: colors.navigation?.selectedText ?? colors.primary.main,
				selectedIcon: colors.navigation?.selectedIcon ?? colors.primary.main,
				sectionLabel: colors.navigation?.sectionLabel ?? colors.text.secondary,
			},
			text: { primary: colors.text.primary, secondary: colors.text.secondary },
			divider: colors.divider?.default ?? alpha(colors.text.primary, colors.mode === 'dark' ? 0.16 : 0.1),
			grey: { 50: alpha(colors.text.primary, 0.06), 300: alpha(colors.text.primary, 0.24) } as any,
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: (theme) => ({
					body: {
						backgroundColor: colors.background.gutter ?? colors.background.canvas ?? colors.background.default,
						backgroundImage:
							colors.background.texture && colors.background.texture !== 'none'
								? `linear-gradient(to bottom, ${colors.background.gutter ?? colors.background.canvas ?? colors.background.default} 0%, ${colors.background.gutterGradientTo ?? colors.background.gutter ?? colors.background.canvas ?? colors.background.default} 100%), ${colors.background.texture}`
								: `linear-gradient(to bottom, ${colors.background.gutter ?? colors.background.canvas ?? colors.background.default} 0%, ${colors.background.gutterGradientTo ?? colors.background.gutter ?? colors.background.canvas ?? colors.background.default} 100%)`,
						fontFamily: appFontFamily,
					},
					...portalScrollbarStyles(theme),
				}),
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						borderRadius: appShape.surfaceRadius,
						border: `1px solid ${alpha(colors.text.primary, colors.mode === 'dark' ? 0.18 : 0.08)}`,
						boxShadow: `0 6px 24px ${alpha(colors.text.primary, colors.mode === 'dark' ? 0.2 : 0.08)}`,
					},
				},
			},
			MuiCard: {
				styleOverrides: {
					root: {
						borderRadius: appShape.surfaceRadius,
						border: `1px solid ${alpha(colors.text.primary, colors.mode === 'dark' ? 0.18 : 0.08)}`,
						boxShadow: `0 8px 28px ${alpha(colors.text.primary, colors.mode === 'dark' ? 0.2 : 0.08)}`,
					},
				},
			},
			MuiAccordion: {
				styleOverrides: {
					root: {
						borderRadius: appShape.surfaceRadius,
						border: `1px solid ${alpha(colors.text.primary, colors.mode === 'dark' ? 0.18 : 0.08)}`,
						boxShadow: `0 4px 18px ${alpha(colors.text.primary, colors.mode === 'dark' ? 0.2 : 0.08)}`,
						marginBottom: 8,
						'&:before': { display: 'none' },
						'&:last-of-type': { marginBottom: 0 },
					},
				},
			},
			MuiTableCell: {
				styleOverrides: {
					root: { color: colors.text.primary, borderBottom: `1px solid ${alpha(colors.text.primary, colors.mode === 'dark' ? 0.1 : 0.12)}` },
					head: { color: colors.text.primary, fontWeight: 600, backgroundColor: colors.background.paper },
				},
			},
			MuiButton: {
				styleOverrides: {
					containedPrimary: {
						backgroundColor: primaryButtonBackground,
						color: primaryButtonText,
						'&:hover': { backgroundColor: primaryButtonHoverBackground },
					},
				},
			},
			MuiTable: { styleOverrides: { root: { backgroundColor: colors.background.paper } } },
			MuiTableRow: { styleOverrides: { root: { '&:hover': { backgroundColor: alpha(colors.primary.main, colors.mode === 'dark' ? 0.05 : 0.04) } } } },
		},
	});

	(theme.components as any).MuiDataGrid = {
		styleOverrides: {
			root: {
				border: 'none',
				borderRadius: appShape.surfaceRadius,
				boxShadow: `0 6px 24px ${alpha(colors.text.primary, colors.mode === 'dark' ? 0.2 : 0.08)}`,
				'& .MuiDataGrid-cell': { color: colors.text.primary, borderBottom: `1px solid ${alpha(colors.text.primary, colors.mode === 'dark' ? 0.1 : 0.12)}` },
				'& .MuiDataGrid-columnHeaders': {
					backgroundColor: colors.background.paper,
					color: colors.text.primary,
					borderBottom: `1px solid ${alpha(colors.text.primary, colors.mode === 'dark' ? 0.15 : 0.16)}`,
				},
				'& .MuiDataGrid-columnHeaderTitle': { color: colors.text.primary, fontWeight: 600 },
				'& .MuiDataGrid-row:hover': { backgroundColor: alpha(colors.primary.main, colors.mode === 'dark' ? 0.05 : 0.04) },
				'& .MuiDataGrid-footerContainer': {
					borderTop: `1px solid ${alpha(colors.text.primary, colors.mode === 'dark' ? 0.1 : 0.12)}`,
					color: colors.text.primary,
				},
				'& .MuiTablePagination-root': { color: colors.text.primary },
				'& .MuiIconButton-root': { color: colors.text.secondary },
				'& a': { color: colors.text.primary, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
			},
		},
	};

	return theme;
};

export const darkMainTheme = createPortalTheme(darkMainColors);
export const darkBlueTheme = createPortalTheme(darkBlueColors);
export const whiteBlueTheme = createPortalTheme(whiteBlueColors);
export const lightWarmTheme = createPortalTheme(lightWarmColors);
export const randolWarmTheme = createPortalTheme(randolWarmColors);
export const randolBrandedTheme = createPortalTheme(randolBrandedColors);
export const portalThemes: Record<PortalThemeName, Theme> = {
	'dark-main': darkMainTheme,
	'dark-blue': darkBlueTheme,
	'white-blue': whiteBlueTheme,
	'light-warm': lightWarmTheme,
	'randol-warm': randolWarmTheme,
	'randol-branded': randolBrandedTheme,
};

// Retained for existing consumers and test fixtures.
export const defaultTheme = darkMainTheme;
